import React, { useState, useEffect } from 'react';
import {
  Box, Button, FormControlLabel, Switch, TextField, Typography,
} from '@mui/material';
import { useNavigate} from 'react-router-dom';
import { ArrowBack, Save } from '@mui/icons-material';
import Widget from '../components/Widget';
import { UserSelector } from '../../../components/UserSelector';
import { IAccount } from '../../../resource';
import apis from '../../../apis';
import { useToastContext } from '../../../components/Toast/toastContext';
import { ROUTES} from '../../../contants';
import styles from '../styles';
import { ITheme } from '../../../resource/interfaces';
import { generateColorSet } from '../../../utils/colorUtils';
import { ThemeSelector } from '../../../components/ThemeSelector';

export const WidgetAddPage = () => {
  const navigate = useNavigate();
  const { activeToast } = useToastContext();

  const [widget, setWidget] = useState<any>(null);
  const [customer, setCustomer] = useState<IAccount | null>(null);
  const [preview, setPreview] = useState(false);
  const [themes, setThemes] = useState<ITheme[]>([]);
  const [themesLoading, setThemesLoading] = useState<boolean>(false);
  const [selectedThemeId, setSelectedThemeId] = useState<string>('');
  const [selectedTheme, setSelectedTheme] = useState<ITheme | null>(null);
  // Popover states removed (ThemeSelector handles preview)
  const [isin, setIsin] = useState<string>('');
  const [name, setName] = useState<string>('');

  const goBack = () => {
    navigate(-1);
  };

  const loadThemes = async () => {
    setThemesLoading(true);
    try {
      // For demo purposes, create some sample themes if API is not available
      if (!apis.theme || typeof apis.theme.getThemes !== 'function') {
        // Demo data
        const demoThemes: ITheme[] = [
          {
            _id: '1',
            name: 'Default Blue',
            mainColor: '#1976d2',
            colorSet: generateColorSet('#1976d2'),
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          {
            _id: '2',
            name: 'Forest Green',
            mainColor: '#2e7d32',
            colorSet: generateColorSet('#2e7d32'),
            isActive: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          {
            _id: '3',
            name: 'Sunset Orange',
            mainColor: '#ed6c02',
            colorSet: generateColorSet('#ed6c02'),
            isActive: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        ];
        setThemes(demoThemes);
        setSelectedThemeId('1'); // Set default theme
        return;
      }

      const result = await apis.theme.getThemes();
      if (result.success) {
        setThemes(result.themes);
        if (result.themes.length > 0) {
          setSelectedThemeId(result.themes[0]._id || '');
        }
      } else {
        activeToast({ type: 'error', text: result.msg || 'Failed to load themes' });
      }
    } catch (error) {
      activeToast({ type: 'error', text: 'Failed to load themes' });
    } finally {
      setThemesLoading(false);
    }
  };

  useEffect(() => {
    loadThemes();
  }, []);

  useEffect(() => {
    // Update selectedTheme when selectedThemeId changes
    const theme = themes.find(t => t._id === selectedThemeId);
    setSelectedTheme(theme || null);
  }, [selectedThemeId, themes]);

  const onChangeWidget = (widget) => {
    setWidget({
      ...widget,
      theme: selectedThemeId,
      isin: isin,
      name: name,
    });
  };

  const addNewWidget = () => {
    if (customer) {
      apis.cdn.saveCdnWidget(customer.id, widget)
        .then((res: any) => {
          if (res.status) {
            activeToast({
              type: 'success',
              text: res.msg,
            });
            navigate(ROUTES.MANAGE_WIDGETS);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  const handleChangeThemeId = (themeId: string) => {
    setSelectedThemeId(themeId);
  };

  const handleChangeIsin = (e) => {
    setIsin(e.target.value);
  }

  const handleChangeName = (e) => {
    setName(e.target.value);
  };

  // ColorSetPreview and popover handlers removed (ThemeSelector includes its own preview UI)

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={20}>
        <Typography variant="h5">Add New Widget</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={goBack}>Back</Button>
      </Box>
      <Box display="flex" alignItems="center" marginY={20}>
        <UserSelector value={customer} onChange={setCustomer} />
        <Box marginLeft={20} display="flex" alignItems="center" gap={30}>
          <TextField
            label="NAME"
            fullWidth
            value={name || ""}
            onChange={handleChangeName}
            sx={styles.isinInput}
          />
          <TextField
            label="ISIN"
            type="string"
            fullWidth
            value={isin || ""}
            onChange={handleChangeIsin}
            sx={styles.isinInput}
          />
        </Box>
        <FormControlLabel
          sx={{ marginLeft: 'auto' }}
          onChange={() => setPreview(!preview)}
          control={<Switch checked={preview} color="primary" />}
          label="Preview"
        />
      </Box>
      <Box display="flex" justifyContent="space-between" marginY={20}>
        <ThemeSelector
          selectedThemeId={selectedThemeId}
          onChange={handleChangeThemeId}
        />
      </Box>

      <Widget
        isin={isin}
        widget={widget}
        onChangeWidget={onChangeWidget}
        preview={preview}
        theme={selectedTheme}
        themeColor={selectedTheme?.mainColor || '#1976d2'}
      />
      <Box display="flex" justifyContent="flex-end">
        <Button
          startIcon={<Save />}
          onClick={addNewWidget}
          disabled={!customer || !widget}
        >
          Save
        </Button>
      </Box>
    </Box>
  )
};
